import { Grid, Skeleton, Stack, Typography } from "@mui/material";
import { AxiosError } from "axios";
import { useEffect, useState } from "react";
import { useLocation } from "react-router";
import { API_URLS } from "src/api/api-urls";
import useApiServices from "src/api/use-api-services";
import AppContainer from "src/components/common/app-container";
import { PlatformsEnum } from "src/constants/platforms";
import PlatformHeader from "src/sections/dashboards/shared/filters/platform-header";
import { IActivityItemDto } from "src/types/activities";
import { DashboardType } from "src/types/types";
import NoData from "./no-data/no-data";

const DashboardViewLayout = ({
  type,
  isLoading,
  platform,
  children,
  pageTitle,
  error,
}: {
  type: DashboardType;
  isLoading: boolean;
  platform: PlatformsEnum;
  children: React.ReactNode;
  pageTitle: string;
  error: AxiosError<any> | null;
}) => {
  const { state }: { state: { activity: IActivityItemDto } | undefined } = useLocation();
  const { useGetItemApi } = useApiServices();

  const [activity, setActivity] = useState<IActivityItemDto | undefined>(state?.activity);

  // If there is no state, it means that the analysis page has been
  // reached from notifications, so it's definitely completed
  const isActivityCompleted = activity?.status ? activity?.status === "completed" : true;

  const { data: activityData, isFetching: isLoadingActivity } = useGetItemApi<{
    activity: IActivityItemDto;
  }>({
    url: API_URLS.ACTIVITY,
    enabled: !isActivityCompleted,
    id: activity?.id,
    refetchInterval: 5000,
    onSuccess: (data: { activity: IActivityItemDto }) => {
      setActivity(data.activity);
    },
  });

  useEffect(() => {
    setActivity(state?.activity);
  }, [state]);

  return (
    <AppContainer pageTitle={pageTitle}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          {
            <PlatformHeader
              platform={platform}
              type={type}
              error={error}
              isActivityCompleted={isActivityCompleted}
            />
          }
        </Grid>
        {error ? (
          <Grid item xs={12}>
            <NoData message={error.response?.data?.errors[0].message || ""} />
          </Grid>
        ) : isActivityCompleted ? (
          <Grid item xs={12}>
            <AppContainer isLoading={isLoading} pageTitle={pageTitle} noContainer>
              {children}
            </AppContainer>
          </Grid>
        ) : (
          <Grid item xs={12}>
            <Stack alignItems="center" sx={{ width: 1 }}>
              <Skeleton variant="text" height={30} width={400} sx={{ mb: 0.5, borderRadius: 1 }} />
              <Skeleton
                variant="rectangular"
                height={40}
                width={500}
                sx={{ mb: 1, borderRadius: 1 }}
                color="primary.darker"
              />

              <Typography variant="h3">Loading {activityData?.activity?.progress}% ...</Typography>
              <Typography variant="caption" color="text.disabled">
                We need some time to prepare the report
              </Typography>
            </Stack>
          </Grid>
        )}
      </Grid>
    </AppContainer>
  );
};

export default DashboardViewLayout;
